/**
 * The "who is buying" step before a Buy now payment.
 *
 * Checkout needs the shopper's name and email, and asking first is also what
 * lets the store remind a shopper who leaves before paying (an abandoned
 * checkout needs someone to remind). A cart checkout asks inside its own
 * window; Buy now, khaime.checkout() and the pricing table ask here.
 *
 * Remembered on this device so a returning shopper only taps Continue.
 */

export interface CheckoutCustomer {
  email: string;
  name: string;
}

const REMEMBER_KEY = 'khaime:customer';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(email: string): boolean {
  return EMAIL.test(email.trim());
}

function remembered(): Partial<CheckoutCustomer> {
  try {
    return JSON.parse(localStorage.getItem(REMEMBER_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function remember(customer: CheckoutCustomer): void {
  try {
    localStorage.setItem(REMEMBER_KEY, JSON.stringify(customer));
  } catch {
    // Private mode: they type it again next time.
  }
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * Ask for the shopper's name and email. Resolves with them, or null when the
 * shopper closes the step.
 */
export function askForCustomer(options: { productTitle?: string; accent?: string } = {}): Promise<CheckoutCustomer | null> {
  return new Promise(resolve => {
    const saved = remembered();
    const accent = /^#[0-9a-f]{3,8}$/i.test(options.accent || '') ? options.accent! : '#111827';
    const overlay = document.createElement('div');
    overlay.setAttribute('data-khaime-customer-step', '');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:2147483646;background:rgba(0,0,0,.5);display:flex;align-items:flex-end;justify-content:center;padding:16px;font-family:system-ui,sans-serif';
    overlay.innerHTML = `
<form role="dialog" aria-modal="true" aria-labelledby="khaime-cs-title" novalidate
  style="background:#fff;color:#111;width:100%;max-width:420px;border-radius:16px;padding:20px;box-shadow:0 10px 40px rgba(0,0,0,.25);margin:auto">
  <h2 id="khaime-cs-title" style="margin:0 0 4px;font-size:18px;font-weight:600">Your details</h2>
  <p style="margin:0 0 16px;font-size:14px;color:#555">${options.productTitle ? `For ${esc(options.productTitle)}. ` : ''}Your receipt goes to this email.</p>
  <label style="display:block;font-size:14px;font-weight:600;margin-bottom:4px" for="khaime-cs-name">Full name</label>
  <input id="khaime-cs-name" name="name" autocomplete="name" required value="${esc(saved.name || '')}"
    style="width:100%;box-sizing:border-box;font-size:16px;padding:12px;border:1px solid #ccc;border-radius:10px;margin-bottom:12px">
  <label style="display:block;font-size:14px;font-weight:600;margin-bottom:4px" for="khaime-cs-email">Email</label>
  <input id="khaime-cs-email" name="email" type="email" inputmode="email" autocomplete="email" required value="${esc(saved.email || '')}"
    style="width:100%;box-sizing:border-box;font-size:16px;padding:12px;border:1px solid #ccc;border-radius:10px">
  <p data-error role="alert" style="min-height:20px;margin:8px 0;font-size:14px;color:#b42318"></p>
  <button type="submit" style="width:100%;min-height:48px;border:0;border-radius:999px;background:${accent};color:#fff;font-size:16px;font-weight:600;cursor:pointer">Continue to payment</button>
  <button type="button" data-cancel style="width:100%;min-height:44px;margin-top:8px;border:0;background:none;color:#555;font-size:15px;cursor:pointer">Cancel</button>
</form>`;

    const form = overlay.querySelector('form') as HTMLFormElement;
    const nameInput = overlay.querySelector('#khaime-cs-name') as HTMLInputElement;
    const emailInput = overlay.querySelector('#khaime-cs-email') as HTMLInputElement;
    const error = overlay.querySelector('[data-error]') as HTMLElement;

    const close = (value: CheckoutCustomer | null) => {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(value);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(null);
    };

    form.addEventListener('submit', e => {
      e.preventDefault();
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      if (!name) {
        error.textContent = 'Please enter your name.';
        nameInput.focus();
        return;
      }
      if (!isValidEmail(email)) {
        error.textContent = 'Please enter a valid email, like name@example.com.';
        emailInput.focus();
        return;
      }
      const customer = { name, email };
      remember(customer);
      close(customer);
    });
    overlay.querySelector('[data-cancel]')!.addEventListener('click', () => close(null));
    overlay.addEventListener('click', e => {
      if (e.target === overlay) close(null);
    });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    (saved.name ? emailInput : nameInput).focus();
  });
}
